/**
 * Configuration des étapes du tutoriel interactif de création d'utilisateur externe
 *
 * L'ordre suit le flow réel du wizard :
 *   Step 1 (Informations : société + infos perso) → Step 2 (Rôle & permissions)
 */

import type { ExternalUserFormState } from '@/features/external-users/hooks/useExternalUserFormWizard';

export interface TutorialStep {
  id: string;
  fieldRef: string | string[];
  title: string;
  content: string;
  validation: (formData: ExternalUserFormState) => boolean;
  wizardStep: number;
  fieldType: 'text' | 'select' | 'radio' | 'number';
  placement?: 'auto' | 'top';
}

export const TUTORIAL_STEPS: TutorialStep[] = [
  // ========== Step 1 - Informations ==========
  {
    id: 'society',
    fieldRef: 'external-user-society-block',
    title: 'Société principale',
    content:
      'Sélectionnez la société à laquelle cet utilisateur sera rattaché. Si la société possède des filiales, vous pourrez lui donner accès à d\'autres sociétés du groupe.',
    validation: (formData) => !!formData.primarySocietyId,
    wizardStep: 1,
    fieldType: 'select',
  },
  {
    id: 'identity',
    fieldRef: 'external-user-identity-block',
    title: 'Identité',
    content:
      'Renseignez la civilité (optionnelle), le prénom et le nom de l\'utilisateur. Ces informations seront visibles dans son profil.',
    validation: (formData) =>
      !!formData.firstname &&
      formData.firstname.length >= 2 &&
      !!formData.lastname &&
      formData.lastname.length >= 2,
    wizardStep: 1,
    fieldType: 'text',
  },
  {
    id: 'contact',
    fieldRef: 'external-user-contact-block',
    title: 'Contact',
    content:
      'Saisissez l\'adresse email de l\'utilisateur (obligatoire) et son numéro de téléphone (optionnel). L\'email servira d\'identifiant de connexion.',
    validation: (formData) =>
      !!formData.email && formData.email.includes('@'),
    wizardStep: 1,
    fieldType: 'text',
  },
  {
    id: 'position',
    fieldRef: 'external-user-position-block',
    title: 'Poste / Fonction',
    content:
      'Indiquez le poste ou la fonction de l\'utilisateur (optionnel). Cette information est purement informative.',
    validation: () => true,
    wizardStep: 1,
    fieldType: 'text',
  },

  // ========== Step 2 - Rôle & Permissions ==========
  {
    id: 'role',
    fieldRef: 'external-user-role-block',
    title: 'Rôle',
    content:
      'Choisissez le rôle de l\'utilisateur : Administrateur (accès complet à la société) ou Utilisateur (accès limité selon les permissions).',
    validation: (formData) => !!formData.roleType,
    wizardStep: 2,
    fieldType: 'select',
  },
  {
    id: 'permissions',
    fieldRef: 'external-user-permissions-block',
    title: 'Permissions',
    content:
      'Les permissions par défaut sont attribuées selon le rôle choisi. Vous pouvez les personnaliser en cliquant sur "Personnaliser les permissions".',
    validation: (formData) => formData.permissions.length > 0,
    wizardStep: 2,
    fieldType: 'select',
  },
];

/**
 * Obtient les étapes du tutoriel filtrées selon le contexte
 * @param shouldShowSociety - Si true, inclut l'étape société
 */
export function getFilteredExternalUserTutorialSteps(
  shouldShowSociety: boolean,
): TutorialStep[] {
  let steps = [...TUTORIAL_STEPS];

  if (!shouldShowSociety) {
    steps = steps.filter((step) => step.id !== 'society');
  }

  return steps;
}
